/**
 * Plush circle collections — prev/next arrow controls for the category scroller.
 *
 * The list is a native scroll-snap row (swipe works on its own). This adds the
 * left/right arrows, like the product image slider: they only appear when the
 * row actually overflows, each click scrolls by roughly one viewport of tiles,
 * and each arrow disables itself at its end.
 */
(function () {
  'use strict';

  function setup(track) {
    if (track.dataset.arrowsReady === '1') return;
    var scroller = track.closest('.plush-circles__scroller');
    if (!scroller) return;
    var prev = scroller.querySelector('[data-circles-prev]');
    var next = scroller.querySelector('[data-circles-next]');
    if (!prev || !next) return;
    track.dataset.arrowsReady = '1';

    // Scroll distance per click: ~2 tiles, but at least most of a viewport.
    function amount() {
      var item = track.querySelector('.plush-circles__item');
      var w = item ? item.getBoundingClientRect().width : 160;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 24;
      return Math.max((w + gap) * 2, track.clientWidth * 0.75);
    }

    // scrollWidth and clientWidth only change when the row is re-laid out, so
    // they are measured on resize and after load rather than on every scroll.
    // This row is also auto-scrolled, which fires a scroll event every frame;
    // re-reading geometry there forced a synchronous layout each time, and that
    // read-write-read loop was the single largest main-thread cost on the home
    // page. Only scrollLeft is read per scroll now, and the two writes happen
    // only when their value actually changes.
    var trackWidth = 0;
    var viewWidth = 0;
    var hadArrows = null;

    function measure() {
      trackWidth = track.scrollWidth;
      viewWidth = track.clientWidth;
    }

    function update() {
      var scrollable = trackWidth > viewWidth + 4;
      if (scrollable !== hadArrows) {
        scroller.classList.toggle('has-arrows', scrollable);
        hadArrows = scrollable;
      }
      var left = track.scrollLeft;
      var atStart = left <= 2;
      var atEnd = left + viewWidth >= trackWidth - 2;
      if (prev.disabled !== atStart) prev.disabled = atStart;
      if (next.disabled !== atEnd) next.disabled = atEnd;
    }

    function remeasure() {
      measure();
      update();
    }

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -amount(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: amount(), behavior: 'smooth' });
    });

    var raf;
    track.addEventListener(
      'scroll',
      function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      },
      { passive: true }
    );
    window.addEventListener('resize', remeasure);
    // Images loading can change scrollWidth — recheck once they're in.
    window.addEventListener('load', remeasure);

    // plush-auto-scroll.js clones the whole set into this same track and strips
    // the clones again on first interaction, both of which change scrollWidth.
    // Without this the cached width would be wrong and the arrows would disable
    // at the wrong moment. Fires twice in a page's life, not per frame.
    if ('MutationObserver' in window) {
      new MutationObserver(remeasure).observe(track, { childList: true });
    }

    remeasure();
  }

  function boot() {
    document.querySelectorAll('[data-circles-track]').forEach(setup);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
